import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { CardMetadata as LegacyCardMetadata } from './Legacy/CardMetadata';

export type CardMetadataItem = NonNullable<CardMetadataProps['items']>[number];
export type CardMetadataProps = React.ComponentProps<typeof LegacyCardMetadata>;

const styles = cssMap({
	metadata: {
		gridArea: 'metadata',
		minWidth: 0,
		paddingInlineStart: token('space.150'),
	},
	interactive: { position: 'relative', zIndex: 200, pointerEvents: 'auto' },
	item: { minWidth: 0 },
});

export const CardMetadata = (props: CardMetadataProps): React.JSX.Element => {
	if (!expVal('jira_browse_agent_modal_update', 'isEnabled', false)) {
		return <LegacyCardMetadata {...props} />;
	}

	const { items, children, isInteractive = false, testId } = props;

	return (
		<Box xcss={cx(styles.metadata, isInteractive && styles.interactive)} testId={testId}>
			{items ? (
				<Inline space="space.075" alignBlock="center" alignInline="end" shouldWrap>
					{items.map(({ key, icon: Icon, iconColor, label }, index) => (
						<React.Fragment key={key}>
							{index > 0 ? (
								<Text size="small" color="color.text.subtle">
									·
								</Text>
							) : null}
							<Inline xcss={styles.item} space="space.050" alignBlock="center">
								{Icon ? (
									<Icon label="" size="small" color={iconColor ?? token('color.icon.subtlest')} />
								) : null}
								<Text size="small" color="color.text.subtle" maxLines={1}>
									{label}
								</Text>
							</Inline>
						</React.Fragment>
					))}
				</Inline>
			) : (
				<Inline alignInline="end" alignBlock="center">
					{children}
				</Inline>
			)}
		</Box>
	);
};
